import * as React from 'react';
import { useCallback } from 'react';

import { MAX_QUESTION_TEXT_LENGTH } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtom } from 'jotai';
import { useDispatch } from 'react-redux';

import {
  ImageAddMethod,
  ImageUploadContext,
} from '~analytics/adapters/ImageEventAdapterTypes';
import { Checkbox } from '~shared/components/Checkbox';
import { NumericInput } from '~shared/components/NumericInput';
import QuillEditor from '~shared/components/quill/QuillEditor';
import { QuillTextChangeHandler } from '~shared/components/quill/quillHooks';
import { RichTextRenderer } from '~shared/components/RichTextRenderer';
import { selectedQuestionIds } from '~teacher/components/QuizEditor/atoms/selectedQuestionAtom';
import { useSelectHasQuestionMedia } from '~teacher/components/QuizQuestion/helpers/useSelectHasQuestionMedia';
import { useUploadImage } from '~teacher/hook/upload/useUploadImage';
import { updateQuestionImage } from '~teacher/slices/quiz/quizSlice';

interface Props {
  questionId: number | string;
  order: number;
  text: string;
  weight: number;
  placeholder: string;
  /** Is the question in Edit mode? */
  isEditing: boolean;
  /** is the question disabled? */
  isDisabled: boolean;
  onQuestionTextChange: (newText: string) => void;
  onChangeWeight: (newWeight: number) => boolean;
  showAdvancedQuizEditor: boolean;
  isLocked: boolean;
}

function useQuestionTextId(questionId: unknown) {
  return `question-text-id-${questionId}`;
}

export function QuestionText({
  isLocked,
  questionId,
  order,
  text,
  weight,
  placeholder,
  isEditing = false,
  isDisabled,
  onChangeWeight,
  showAdvancedQuizEditor,
  onQuestionTextChange,
}: Props) {
  const quillRef = React.useRef<HTMLDivElement>(null);
  const uploadImage = useUploadImage({ context: ImageUploadContext.Question });
  const dispatch = useDispatch();

  const hasQuestionMedia = useSelectHasQuestionMedia({
    questionId: `${questionId}`,
  });

  const handleUploadImage = useCallback(
    (file) => {
      uploadImage.mutate(
        { files: [file], addMethod: ImageAddMethod.CopyPaste },
        {
          onSuccess: (url) => {
            dispatch(updateQuestionImage({ url: url }));
          },
        }
      );
    },
    [dispatch, uploadImage]
  );

  useHandleQuestionTextPaste({
    quillRef: quillRef,
    isDisabled: hasQuestionMedia,
    handleUploadImage: handleUploadImage,
  });

  const questionTextId = useQuestionTextId(questionId);

  const handleTextBoxChange: QuillTextChangeHandler = ({
    html,
  }: {
    html: string;
    text: string;
  }) => {
    onQuestionTextChange(html);
  };

  const weightLabelText =
    weight === 1 ? translate('point') : translate('points');

  return (
    <div
      className="grid grid-cols-question-fields-question-editor max-w-5xl p-0"
      data-quiz-question-text
    >
      <div className="flex flex-col align-center justify-top py-2">
        {!isLocked ? <SelectQuestionCheckbox questionId={questionId} /> : null}
      </div>
      <div
        id={questionTextId}
        className="flex flex-none items-center justify-center w-12 h-12 font-display text-md font-700 text-blue-900"
      >
        <span className="sr-only">{translate('Question')}</span>
        {order}.
      </div>
      <div className="col-start-3 min-w-0 ml-2">
        {!isEditing || isDisabled ? (
          text ? (
            <RichTextRenderer
              className="py-2 font-sans text-blue-900 text-base leading-8 ql-editor"
              htmlText={text}
            />
          ) : null
        ) : (
          <div className="lg:flex">
            <QuillEditor
              initialHtml={text}
              placeholder={placeholder}
              textFieldLabel={translate('Question')}
              onTextChange={handleTextBoxChange}
              isReadOnly={!isEditing || isDisabled}
              maxLength={MAX_QUESTION_TEXT_LENGTH}
              ref={quillRef}
            />
            <div className="flex-none max-w-50 mt-4 lg:mt-0 lg:ml-4">
              <NumericInput
                min={0}
                max={99.99}
                precision={2}
                placeholder={'1'}
                textAlign={showAdvancedQuizEditor ? 'left' : 'right'}
                bgColor="white"
                value={weight?.toString()}
                onChange={onChangeWeight}
                disabled={isDisabled}
                internalLabel={weightLabelText}
                showProBadge={showAdvancedQuizEditor ? 'never' : 'focus'}
                allowEmptyString={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SelectQuestionCheckbox({
  questionId,
}: {
  questionId: number | string;
}) {
  const [selectedQuestion] = useAtom(selectedQuestionIds.valueAtom);
  const [, addQuestion] = useAtom(selectedQuestionIds.addAtom);
  const [, removeQuestion] = useAtom(selectedQuestionIds.removeAtom);

  const questionTextId = useQuestionTextId(questionId);

  return (
    <Checkbox
      size="medium"
      aria-labelledby={questionTextId}
      checked={selectedQuestion.has(`${questionId ?? ''}`)}
      onChange={(event) => {
        if ((event.target as any).checked) {
          addQuestion(`${questionId}`);
        } else {
          removeQuestion(`${questionId}`);
        }
      }}
    />
  );
}

function useHandleQuestionTextPaste({
  quillRef,
  handleUploadImage,
  isDisabled,
}: {
  quillRef: React.RefObject<HTMLDivElement>;
  handleUploadImage: (file: File) => void;
  isDisabled: boolean;
}) {
  React.useEffect(() => {
    const editor = quillRef.current;

    const handlePaste = async (e: ClipboardEvent) => {
      if (isDisabled) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            try {
              handleUploadImage(file);
            } catch (error) {
              console.error('Failed to upload pasted image:', error);
            }
          }
          break;
        }
      }
    };

    editor?.addEventListener('paste', handlePaste);

    return () => editor?.removeEventListener('paste', handlePaste);
  }, [handleUploadImage, isDisabled, quillRef]);
}
